// src/pages/BlogPost.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import ReaderGate from '../components/ReaderGate';
import LazyImage from '../components/LazyImage';
import '../Blog.css';

// SEO helper functions (outside component to avoid recreating)
const updateMetaTag = (name, content, attr = 'property') => {
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const removeMetaTag = (name, attr = 'property') => {
  const tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (tag) {
    tag.remove();
  }
};

const getFirstParagraphStatic = (content) => {
  const text = content.replace(/<[^>]*>/g, '');
  const sentences = text.split(/[.!?]+/);
  let preview = '';
  for (let i = 0; i < sentences.length && preview.length < 350; i++) {
    preview += sentences[i] + '. ';
  }
  return preview.trim();
};

// Apply SEO tags immediately when called (not dependent on React lifecycle)
const applySEOTags = (post) => {
  if (!post) return () => { };

  const originalTitle = document.title;
  document.title = post.seoTitle || `${post.title} | Avenir Insights`;

  let metaDescription = document.querySelector('meta[name="description"]');
  const originalDescription = metaDescription?.getAttribute('content') || '';

  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    document.head.appendChild(metaDescription);
  }
  const description = post.seoDescription || post.subtitle || getFirstParagraphStatic(post.content).substring(0, 160);
  metaDescription.setAttribute('content', description);

  // Open Graph tags
  updateMetaTag('og:title', post.seoTitle || post.title);
  updateMetaTag('og:description', description);
  updateMetaTag('og:type', 'article');
  updateMetaTag('og:url', window.location.href);
  if (post.coverImage) {
    updateMetaTag('og:image', post.coverImage);
  }

  // Twitter Card tags
  updateMetaTag('twitter:card', 'summary_large_image');
  updateMetaTag('twitter:title', post.seoTitle || post.title);
  updateMetaTag('twitter:description', description);
  if (post.coverImage) {
    updateMetaTag('twitter:image', post.coverImage);
  }

  // Article specific meta tags
  updateMetaTag('article:published_time', post.createdAt, 'property');
  if (post.updatedAt) {
    updateMetaTag('article:modified_time', post.updatedAt, 'property');
  }
  if (post.authorName) {
    updateMetaTag('article:author', post.authorName, 'property');
  }
  if (post.category) {
    updateMetaTag('article:section', post.category, 'property');
  }
  if (post.tags && post.tags.length > 0) {
    post.tags.forEach(tag => {
      updateMetaTag('article:tag', tag, 'property');
    });
  }

  // Return cleanup function
  return () => {
    document.title = originalTitle;
    if (metaDescription) {
      metaDescription.setAttribute('content', originalDescription);
    }
    removeMetaTag('og:title');
    removeMetaTag('og:description');
    removeMetaTag('og:type');
    removeMetaTag('og:url');
    removeMetaTag('og:image');
    removeMetaTag('twitter:card');
    removeMetaTag('twitter:title');
    removeMetaTag('twitter:description');
    removeMetaTag('twitter:image');
    removeMetaTag('article:published_time');
    removeMetaTag('article:modified_time');
    removeMetaTag('article:author');
    removeMetaTag('article:section');
  };
};

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, hasReaderAccess } = useAuth();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch post and apply SEO immediately (independent of auth)
  useEffect(() => {
    let cleanup = () => { };

    const fetchAndApplySEO = async () => {
      try {
        const docRef = doc(db, 'posts', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const postData = { id: docSnap.id, ...docSnap.data() };
          // Apply SEO tags immediately, before setting state
          cleanup = applySEOTags(postData);
          setPost(postData);
        } else {
          setError('Article not found');
        }
      } catch (err) {
        console.error('Error fetching post:', err);
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    fetchAndApplySEO();

    return () => cleanup();
  }, [id]);

  // Check access separately (depends on auth context)
  useEffect(() => {
    if (post) {
      setIsUnlocked(hasReaderAccess());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, currentUser]);

  const handleUnlock = () => {
    setIsUnlocked(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'posts', id));
      navigate('/blog');
    } catch (err) {
      console.error('Error deleting post:', err);
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]*>/g, '');
    const wordCount = text.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  const getFirstParagraph = getFirstParagraphStatic;

  const ADMIN_EMAILS = ['ari@avenir.com.ai'];
  const isAuthor = currentUser && post && (
    currentUser.uid === post.authorId ||
    ADMIN_EMAILS.includes(currentUser.email)
  );

  if (loading) {
    return (
      <div className="page-content">
        <div className="blog-container">
          <div className="blog-loading">
            <div className="loading-spinner"></div>
            <p>Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="blog-container">
          <div className="blog-error">
            <h2>{error}</h2>
            <Link to="/blog" className="btn btn-primary">Back to Blog</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <article className="blog-post-container">
        {/* Back Link */}
        <Link to="/blog" className="back-link">
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          All Articles
        </Link>

        {/* Article Header */}
        <header className="article-header">
          {post.category && (
            <span className="article-category">{post.category}</span>
          )}
          <h1 className="article-title">{post.title}</h1>
          {post.subtitle && (
            <p className="article-subtitle">{post.subtitle}</p>
          )}

          <div className="article-meta">
            <div className="author-info">
              {post.authorPhotoURL ? (
                <LazyImage
                  src={post.authorPhotoURL}
                  alt={post.authorName}
                  className="author-avatar-img large"
                  style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <div className="author-avatar large">
                  {post.authorName?.charAt(0) || 'A'}
                </div>
              )}
              <div className="author-details">
                <span className="author-name">{post.authorName || 'Avenir Team'}</span>
                <div className="meta-row">
                  <span className="post-date">{formatDate(post.createdAt)}</span>
                  <span className="meta-separator">·</span>
                  <span className="read-time">{calculateReadTime(post.content)}</span>
                </div>
              </div>
            </div>

            {isAuthor && (
              <div className="author-actions">
                <Link to={`/blog/edit/${post.id}`} className="action-btn edit-btn">
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </Link>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="action-btn delete-btn"
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Cover Image */}
        {post.coverImage && (
          <div className="article-cover">
            <LazyImage src={post.coverImage} alt={post.title} />
          </div>
        )}

        {/* Article Content */}
        {!isUnlocked ? (
          <div className="article-content-wrapper gated">
            {/* Show preview text */}
            <div className="article-preview">
              <p>{getFirstParagraph(post.content)}</p>
            </div>

            {/* Fade effect */}
            <div className="content-fade"></div>

            {/* Reader Gate */}
            <ReaderGate onUnlock={handleUnlock} />
          </div>
        ) : (
          <>
            <div className="article-content-wrapper">
              <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="article-tags">
                {post.tags.map((tag, index) => (
                  <span key={index} className="tag">{tag}</span>
                ))}
              </div>
            )}

            {/* Share Section */}
            <div className="article-footer">
              <div className="share-section">
                <span className="share-label">Share this article</span>
                <div className="share-buttons">
                  <button
                    className="share-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      alert('Link copied to clipboard!');
                    }}
                  >
                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                    Copy Link
                  </button>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn"
                  >
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                    Twitter
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn"
                  >
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h3>Delete Article</h3>
              <p>Are you sure you want to delete "{post.title}"? This action cannot be undone.</p>
              <div className="modal-actions">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn btn-secondary"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="btn btn-danger"
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPost;